import * as yup from "yup";
import {
    BackdropCircularProgress,
    DeleteConfirmationDialog,
    EnhancedSnackbar,
    StandardPanel,
} from "@websential/cosmic-v20";
import { Grid, useTheme } from "@mui/material";
import {
    CLIENT_SETUP_SCREEN_SLUG,
    INITIAL_PAGE_SIZE,
    INITIAL_SNACKBAR_DATA,
    ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";

import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    useNavigateToLogout,
} from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { IAppState } from "../../store/reducers";

import { setSelectedActivityLogActions } from "../../store/actions/activityLogActions";
import { setSelectedDashboardApisData } from "../../store/actions/dashboardApisDataActions";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";
import { EarningSummarySetupValidationSchema } from "./validator";
import { EarningSummarySetupToolbar } from "./EarningSummarySetupToolbar";
import { EarningSummaryService } from "../../services/EarningSummaryService";
import { IEarningSummary } from "../../models/EarningSummary";
import { TwoColumnLayout } from "./TwoColumnLayout";
import { EarningSummaryHeaderPanel } from "./EarningSummaryHeaderPanel";
import { EmployeeService } from "../../services/EmployeeService";
import { PayrollService } from "../../services/PayrollService";
import { NotesInfoFieldsPanel } from "./Panels/NotesInfoFieldsPanel";
import { DeductionsInfoFieldsTable } from "./Panels/DeductionsInfoFieldsTable/DeductionsInfoFieldsTable";
import { DeductionsAtSourceInfoFieldsTable } from "./Panels/DeductionsAtSourceInfoFieldsTable/DeductionsAtSourceInfoFieldsTable";
import { NonPeriodicPaymentInfoFieldsTable } from "./Panels/NonPeriodicPaymentInfoFieldsTable/NonPeriodicPaymentInfoFieldsTable";
import { EDREarningsCashFieldsTable } from "./Panels/EDREarningsCashFieldsTable/EDREarningsCashFieldsTable";
import { EDRDeductionsNonCashFieldsTable } from "./Panels/EDRDeductionsNonCashFieldsTable/EDRDeductionsNonCashFieldsTable";
import { EarningsInfoFieldsTable } from "./Panels/EarningsInfoFieldsTable/EarningsInfoFieldsTable";
import { EarningsService } from "../../services/EarningsService";


// import { StandardPanel } from "./StandardPanel";
// import { TwoColumnLayout } from "./TwoColumnLayout";

export interface IEarningSummarySetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "employee-single-data-record";
const PAY_FREQUENCY_DDL_LIST_QUERY_KEY = "pay-frequency-ddl";
const PAYROLL_PERIODS_DDL_LIST_QUERY_KEY = "payroll-periods-ddl";
const ACTIVE_EMPLOYEES_BY_PERIODS_DDL_LIST_QUERY_KEY = "payroll-active-employee-periods-ddl";
const EMPLOYEE_PAYROLL_EARNING_sUMMARY = "employee-payroll-earning-summary";


const useGetEmployeePayrollEarningSummary = (
    clientId: string,
    employeeId: string | null,
    payPeriodFrequencyId: string | null,
    payPeriodEndDate: string | null,
    navigateToLogout: (path: string) => void
) =>
    useQuery({
        queryKey: [
            EMPLOYEE_PAYROLL_EARNING_sUMMARY,
            clientId,
            employeeId,
            payPeriodFrequencyId,
            payPeriodEndDate,

        ],
        queryFn: (data) => {

            return EarningSummaryService.getEmployeePayrollEarningSummary(
                clientId,
                employeeId,
                payPeriodFrequencyId,
                payPeriodEndDate,
                navigateToLogout,
            );
        },
        refetchOnWindowFocus: false,
    });

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
    useQuery({
        queryKey: [PAY_FREQUENCY_DDL_LIST_QUERY_KEY, clientId, taxYear],
        queryFn: (data) => {
            return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
        },
        refetchOnWindowFocus: false,
    });
const useGetPayrollPeriodsDDL = (
    clientId: string, payPeriodId: string | null, taxYear: number
) =>
    useQuery({
        queryKey: [PAYROLL_PERIODS_DDL_LIST_QUERY_KEY, clientId, payPeriodId, taxYear],
        queryFn: (data) => {
            return PayrollService.getPayrollPeriodsDDL(clientId, payPeriodId, taxYear);
        },
        refetchOnWindowFocus: false,
    });
// const useGetActiveEmployeesByPeriodsDDL = (
//     clientId: string, payPeriodId: string | null, payPeriodEndDate: string | null
// ) =>
//     useQuery({
//         queryKey: [ACTIVE_EMPLOYEES_BY_PERIODS_DDL_LIST_QUERY_KEY, clientId, payPeriodId, payPeriodEndDate],
//         queryFn: (data) => {
//             return PayrollService.getActiveEmployeesByPeriodsDDL(clientId, payPeriodId, payPeriodEndDate);
//         },
//         refetchOnWindowFocus: false,
//     });
const useGetEmployeeDDL = (clientId: string) =>
    useQuery({
        queryKey: ["employee-status-ddl", clientId],
        queryFn: () => {
            return EmployeeService.getEmployeeDDL(clientId);
        },
        refetchOnWindowFocus: false,
    });


// Earnings Table
export let earningsItemsInitialData = [
    { id: "1", hoursType: "Reg Hour", rate: null, hours: null, amount: null },
    { id: "2", hoursType: "Over Time 1.5", rate: null, hours: null, amount: null },
    { id: "3", hoursType: "Over Time 2.0", rate: null, hours: null, amount: null },

]

// Deductions Table
export let deductionsItemsInitialData = [
    { id: "1", title: "CPP", income: null, nonPeriodicPay: null, total: null },
    { id: "2", title: "El", income: null, nonPeriodicPay: null, total: null },
    { id: "3", title: "Federal Tax", income: null, nonPeriodicPay: null, total: null },
    { id: "4", title: "Provincial Tax", income: null, nonPeriodicPay: null, total: null },
    { id: "5", title: "Additional Tax", income: null, nonPeriodicPay: null, total: null },
]

// Deductions At Source Table 
export let deductionsAtSourceItemsInitialData = [
    { id: "1", title: "RPP/RRSP/RCA", total: null },
    { id: "2", title: "Union Dues", total: null },
    { id: "3", title: "Deductions by court order", total: null },

]
// Non Periodic Payment Table
export let nonPeriodicPaymentItemsInitialData = [
    { id: "1", title: "Non-Periodic Payment Etc", total: null },
    { id: "2", title: "Bonus", total: null },
    { id: "3", title: "Retroactive Pay Increase", total: null },
]

// EDR Earnings Cash Table
export let edrEarningsCashItemsInitialData = [
    { id: "1", title: "", edrEarningCashLable: null },
]
// EDR Deductions Non Cash Table
export let edrDeductionsNonCashItemsInitialData = [
    { id: "1", title: "", edrDeductionNonCashLabel: null },
]

export const EarningSummary: React.FC<IEarningSummarySetup> = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isPendingActivityLogData = useSelector(
        (state: IAppState) => state.isLoadAxiosCall.isLoading
    );
    const drawerMenuOptions = useSelector(
        (state: IAppState) => state.drawerMenu.menu
    );
    const navigateToLogout = useNavigateToLogout();
    const clientName = useSelector((state: IAppState) => state.client.clientName);
    const clientId = useSelector((state: IAppState) => state.client.id);
    const taxYear = useSelector((state: IAppState) => state.taxYear.year);
    const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
    const queryClient = useQueryClient();
    const { id } = useParams();

    const [editRecordId, setEditRecordId] = useState(id);
    const [selectedpayPeriodIndex, setSelectedPayPeriodIndex] = useState<null | number>(null);
    const [selectedPayPeriodId, setSelectedPayPeriodId] = useState<null | string>(null);
    const [payPeriodStarDate, setPayPeriodStarDate] = useState<null | string>(null);
    const [payPeriodEndDate, setPayPeriodEndDate] = useState<null | string>(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);


    // Earnings Table
    const [earningsTableUpdatedData, setEarningsTableUpdatedData] =
        useState(earningsItemsInitialData);
    const [earningsFieldsDataItems, setEarningsFieldsDataItems] =
        useState(earningsItemsInitialData);

    // Deductions Table
    const [deductionsTableUpdatedData, setDeductionsTableUpdatedData] =
        useState(deductionsItemsInitialData);
    const [deductionsFieldsDataItems, setDeductionsFieldsDataItems] =
        useState(deductionsItemsInitialData);

    // Deductions at Source
    const [deductionsAtSourceTableUpdatedData, setDeductionsAtSourceTableUpdatedData] =
        useState(deductionsAtSourceItemsInitialData);
    const [deductionsAtSourceFieldsDataItems, setDeductionsAtSourceFieldsDataItems] =
        useState(deductionsAtSourceItemsInitialData);

    // Non Periodic Payment 
    const [nonPeriodicPaymentTableUpdatedData, setNonPeriodicPaymentTableUpdatedData] =
        useState(nonPeriodicPaymentItemsInitialData);
    const [nonPeriodicPaymentFieldsDataItems, setNonPeriodicPaymentFieldsDataItems] =
        useState(nonPeriodicPaymentItemsInitialData);

    // Deductions at Source
    const [edrEarningsCashTableUpdatedData, setEdrEarningsCashTableUpdatedData] =
        useState(edrEarningsCashItemsInitialData);
    const [edrEarningsCashFieldsDataItems, setEdrEarningsCashFieldsDataItems] =
        useState(edrEarningsCashItemsInitialData);

    // Deductions at Source
    const [edrDeductionsNonCashTableUpdatedData, setEdrDeductionsNonCashTableUpdatedData] =
        useState(edrDeductionsNonCashItemsInitialData);
    const [edrDeductionsNonCashFieldsDataItems, setEdrDeductionsNonCashFieldsDataItems] =
        useState(edrDeductionsNonCashItemsInitialData);

    const onSnackbarClosed = (
        _event: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarData(INITIAL_SNACKBAR_DATA);
    };

    const formMethods = useForm<any>({
        resolver: yupResolver(EarningSummarySetupValidationSchema),
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    const { isPending, data } = useGetEmployeePayrollEarningSummary(
        clientId,
        selectedEmployeeId,
        selectedPayPeriodId,
        payPeriodEndDate,
        navigateToLogout
    );
    const selectedEmployeePayrollEarningSummary: any | null = data;

    console.log("selectedEmployeePayrollEarningSummary", selectedEmployeePayrollEarningSummary)

    const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
        useGetPayFrequencyDDL(clientId, taxYear);

    const {
        isPending: isPendingPayrollPeriodsDDL,
        data: payrollPeriodsDDL,
    } = useGetPayrollPeriodsDDL(clientId, selectedPayPeriodId, taxYear);

    const { isLoading: isEmployeeDDLLoading, data: employeeDDL } = useGetEmployeeDDL(clientId);
    // const {
    //     isPending: isPendingActiveEmployeesByPeriodsDDL,
    //     data: activeEmployeesByPeriodsDDL,
    // } = useGetActiveEmployeesByPeriodsDDL(clientId, selectedPayPeriodId, payPeriodEndDate);

    // Auto-select last payroll period when DDL loads
    useEffect(() => {
        if (payrollPeriodsDDL && payrollPeriodsDDL.length > 0) {
            let lastItem = payrollPeriodsDDL.at(-1)
            const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === lastItem.value);

            formMethods.setValue('payrollPeriodsDDL', lastItem.endDate)
            formMethods.setValue('payFrequencyDDLItems', lastItem.payScheduleId)
            setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
            // setPayPeriodStarDate(lastItem.startDate)
            setPayPeriodEndDate(lastItem.endDate)
        }
    }, [payrollPeriodsDDL,])

    useEffect(() => {
        if (id && !selectedPayPeriodId) {
            setSelectedPayPeriodId(id)
            formMethods.setValue('payFrequencyDDLItems', id)
        }
    }, [id])

    const onFrequencyChangeHandler = async (selectedItem: any) => {
        setSelectedPayPeriodId(selectedItem.value)
        // let path = `/payroll/earning-summary/${selectedItem.value}`
        let path = `/payroll/earning-summary`
        navigate(path)
    };

    const onPayrollPeriodsDDLChangeHandler = async (selectedItem: any) => {
        const selectedpayrollPeriodValue = formMethods.watch("payrollPeriodsDDL");

        if (selectedpayrollPeriodValue && payrollPeriodsDDL) {
            const selectedPayrollPeriod = payrollPeriodsDDL && payrollPeriodsDDL.find((x: any) => x.endDate === selectedpayrollPeriodValue)
            const selectedPayrollPeriodIndex = payrollPeriodsDDL.findIndex((item: any) => item.value === selectedPayrollPeriod.value);

            setSelectedPayPeriodIndex(selectedPayrollPeriodIndex)
            // setPayPeriodStarDate(selectedPayrollPeriod.startDate)
            setPayPeriodEndDate(selectedPayrollPeriod.endDate)
        }
    };

    const onEmployeeSelectionChangeHandler = (selectedItem: any) => {
        setSelectedEmployeeId(selectedItem.value)
        console.log("selectedItemValue", selectedItem)
    };

    const { isPending: isSubmitting, mutate: submitEarningSummary } =
        useMutation({
            mutationFn: EarningSummaryService.create,
            onSuccess: (res: IEarningSummary | IServerError | IResponse | any) => {

                if (res.status === 200) {
                    setSnackbarData({
                        open: true,
                        message: "Success",
                        severity: "success",
                    });
                    queryClient.invalidateQueries({
                        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
                    });
                    let path = `/payroll/earning-summary/${res.data.id}`;
                    navigate(path);
                } else if (res.status === 201) {
                    setSnackbarData({
                        open: true,
                        message: "Success",
                        severity: "success",
                    });
                    queryClient.invalidateQueries({
                        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
                    });
                    setEditRecordId(res.data.id)
                    let path = `/payroll/earning-summary/${res.data.id}`;
                    navigate(path);
                } else {
                    setSnackbarData({
                        open: true,
                        message: res.message ? res.message : "Something went wrong!",
                        severity: "error",
                    });
                }
            },
            onError: (err) => {
                setSnackbarData({
                    open: true,
                    message: "Something went wrong, try again later!",
                    severity: "error",
                });
            },
        });

    const onSubmit = async (values: any) => {
        console.log("valuesOnSubmit", values)
        // const formatEarningsDataItems =
        //     earningsTableUpdatedData &&
        //     earningsTableUpdatedData
        //         .filter((x: any) => x.title && x.title.trim() !== "").map((x: any) => ({
        //             ...x,
        //             id: undefined,
        //         }));
        // const formatDeductionsDataItems =
        //     deductionsTableUpdatedData &&
        //     deductionsTableUpdatedData
        //         .filter((x: any) => x.title && x.title.trim() !== "").map((x: any) => ({
        //             ...x,
        //             id: undefined,
        //         }));
        // const formatDeductionsAtSourceDataItems =
        //     deductionsAtSourceTableUpdatedData &&
        //     deductionsAtSourceTableUpdatedData
        //         .filter((x: any) => x.title && x.title.trim() !== "").map((x: any) => ({
        //             ...x,
        //             id: undefined,
        //         }));
        // const formatNonPeriodicPaymentDataItems =
        //     nonPeriodicPaymentTableUpdatedData &&
        //     nonPeriodicPaymentTableUpdatedData
        //         .filter((x: any) => x.title && x.title.trim() !== "").map((x: any) => ({
        //             ...x,
        //             id: undefined,
        //         }));
        // const formatEdrEarningsCashDataItems =
        //     edrEarningsCashTableUpdatedData &&
        //     edrEarningsCashTableUpdatedData
        //         .filter((x: any) => x.title && x.title.trim() !== "").map((x: any) => ({
        //             ...x,
        //             id: undefined,
        //         }));
        // const formatEdrDeductionNonCashDataItems =
        //     edrDeductionsNonCashTableUpdatedData &&
        //     edrDeductionsNonCashTableUpdatedData
        //         .filter((x: any) => x.title && x.title.trim() !== "").map((x: any) => ({
        //             ...x,
        //             id: undefined,
        //         }));
        // const payload = {
        //     clientId: clientId,
        //     // id: id && id,
        //     navigateToLogout: navigateToLogout,
        //     earnings: formatEarningsDataItems,
        //     deductions: formatDeductionsDataItems,
        //     deductionsAtSource: formatDeductionsAtSourceDataItems,
        //     nonPeriodicPayments: formatNonPeriodicPaymentDataItems,
        //     edrEarningsCash: formatEdrEarningsCashDataItems,
        //     edrDeductionsNonCash: formatEdrDeductionNonCashDataItems,
        //     savefor: values.savefor,
        //     notes: values.notes,
        //     printOnStub: values.printOnStub,
        // };
        // console.log("payload", payload)
        // console.log("values", values)

        // submitEarningSummary(payload);
    };

    // useEffect(() => {
    //     if (id && selectedDataRecord) {
    //         Object.keys(selectedDataRecord).forEach((fieldName) => {
    //             const fieldValue = selectedDataRecord[fieldName];
    //             formMethods.setValue(fieldName, fieldValue ?? undefined);

    //             // // Earnings
    //             // if (fieldName === 'earnings' && Array.isArray(fieldValue) && fieldValue?.length) {
    //             //     setDeductionsFieldsDataItems(fieldValue);
    //             //     setDeductionsTableUpdatedData(fieldValue);
    //             // } else if (fieldName === 'earnings' && Array.isArray(fieldValue) && fieldValue?.length === 0) {
    //             //     setEarningsFieldsDataItems(earningsItemsInitialData);
    //             //     setEarningsTableUpdatedData(earningsItemsInitialData);
    //             // }
    //             // // Deductions
    //             // if (fieldName === 'deductions' && Array.isArray(fieldValue) && fieldValue?.length) {
    //             //     setDeductionsFieldsDataItems(fieldValue);
    //             //     setDeductionsTableUpdatedData(fieldValue);
    //             // } else if (fieldName === 'deductions' && Array.isArray(fieldValue) && fieldValue?.length === 0) {
    //             //     setDeductionsFieldsDataItems(deductionsItemsInitialData);
    //             //     setDeductionsTableUpdatedData(deductionsItemsInitialData);
    //             // }

    //             // // Deductions At Source
    //             // if (fieldName === 'deductionsAtSource' && Array.isArray(fieldValue) && fieldValue?.length) {
    //             //     setDeductionsAtSourceFieldsDataItems(fieldValue);
    //             //     setDeductionsAtSourceTableUpdatedData(fieldValue);
    //             // } else if (fieldName === 'deductionsAtSource' && Array.isArray(fieldValue) && fieldValue?.length === 0) {
    //             //     setDeductionsAtSourceFieldsDataItems(deductionsAtSourceItemsInitialData);
    //             //     setDeductionsAtSourceTableUpdatedData(deductionsAtSourceItemsInitialData);
    //             // }
    //         });
    //     }
    // }, [id, selectedDataRecord]);


    const expandedLabels = ["Payroll"];
    const expandedEarningSummarySetupItem = "Earning Summary";
    const enhanceData = (data: any[], parentLabel?: string): any[] => {
        return data.map((item) => {
            let isExpanded = expandedLabels.includes(item.label);
            if (
                parentLabel === "Payroll" &&
                item.label === expandedEarningSummarySetupItem
            ) {
                isExpanded = true;
            }
            const enhancedItem = {
                ...item,
                expanded: item.items ? isExpanded : false,
                items: item.items ? enhanceData(item.items, item.label) : undefined,
                path: item.label === expandedEarningSummarySetupItem && "/payroll/earning-summary",
            };
            return enhancedItem;
        });
    };

    const { isPending: isSubmittingSave, mutate: submitSaveEarningSummary } =
        useMutation({
            mutationFn: EarningSummaryService.create,
            onSuccess: (res: IEarningSummary | IServerError | IResponse | any) => {

                if (res.status === 200) {
                    setSnackbarData({
                        open: true,
                        message: "Success",
                        severity: "success",
                    });
                    queryClient.invalidateQueries({
                        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
                    });
                    let path = `/payroll/earning-summary/${res.data.id}`;
                    navigate(path);
                } else if (res.status === 201) {
                    setSnackbarData({
                        open: true,
                        message: "Success",
                        severity: "success",
                    });
                    queryClient.invalidateQueries({
                        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
                    });
                    setEditRecordId(res.data.id)
                    let path = `/payroll/earning-summary/${res.data.id}`;
                    navigate(path);
                } else {
                    setSnackbarData({
                        open: true,
                        message: res.message ? res.message : "Something went wrong!",
                        severity: "error",
                    });
                }
            },
            onError: (err) => {
                setSnackbarData({
                    open: true,
                    message: "Something went wrong, try again later!",
                    severity: "error",
                });
            },
        });

    const { isPending: isSubmittingSaveAndPayroll, mutate: submitSaveAndPayrollEarningSummary } =
        useMutation({
            mutationFn: EarningSummaryService.createPayroll, // 
            onSuccess: (res: IEarningSummary | IServerError | IResponse | any) => {

                if (res.status === 200) {
                    setSnackbarData({
                        open: true,
                        message: "Success",
                        severity: "success",
                    });
                    queryClient.invalidateQueries({
                        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
                    });
                    let path = `/payroll/earning-summary/${res.data.id}`;
                    navigate(path);
                } else if (res.status === 201) {
                    setSnackbarData({
                        open: true,
                        message: "Success",
                        severity: "success",
                    });
                    queryClient.invalidateQueries({
                        queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
                    });
                    setEditRecordId(res.data.id)
                    let path = `/payroll/earning-summary/${res.data.id}`;
                    navigate(path);
                } else {
                    setSnackbarData({
                        open: true,
                        message: res.message ? res.message : "Something went wrong!",
                        severity: "error",
                    });
                }
            },
            onError: (err) => {
                setSnackbarData({
                    open: true,
                    message: "Something went wrong, try again later!",
                    severity: "error",
                });
            },
        });

    const getEarningSummaryPayload = () => {
        const formatEarningsDataItems =
            earningsTableUpdatedData
                ?.filter(
                    (x: any) =>
                        x.hoursType &&
                        x.hoursType.trim() !== ""
                )
                .map((x: any) => ({
                    ...x,
                    id: undefined,
                }));

        const formatDeductionsDataItems =
            deductionsTableUpdatedData
                ?.filter(
                    (x: any) =>
                        x.title &&
                        x.title.trim() !== ""
                )
                .map((x: any) => ({
                    ...x,
                    id: undefined,
                }));

        const formatDeductionsAtSourceDataItems =
            deductionsAtSourceTableUpdatedData
                ?.filter(
                    (x: any) =>
                        x.title &&
                        x.title.trim() !== ""
                )
                .map((x: any) => ({
                    ...x,
                    id: undefined,
                }));

        const formatNonPeriodicPaymentDataItems =
            nonPeriodicPaymentTableUpdatedData
                ?.filter(
                    (x: any) =>
                        x.title &&
                        x.title.trim() !== ""
                )
                .map((x: any) => ({
                    ...x,
                    id: undefined,
                }));

        const formatEdrEarningsCashDataItems =
            edrEarningsCashTableUpdatedData
                ?.filter(
                    (x: any) =>
                        x.title &&
                        x.title.trim() !== ""
                )
                .map((x: any) => ({
                    ...x,
                    id: undefined,
                }));

        const formatEdrDeductionNonCashDataItems =
            edrDeductionsNonCashTableUpdatedData
                ?.filter(
                    (x: any) =>
                        x.title &&
                        x.title.trim() !== ""
                )
                .map((x: any) => ({
                    ...x,
                    id: undefined,
                }));

        const values = formMethods.getValues();

        return {
            clientId: clientId,
            payPeriodEndDate: payPeriodEndDate,
            payrollSheduleId: selectedPayPeriodId,
            employeeId: selectedEmployeeId,
            navigateToLogout: navigateToLogout,

            earnings: formatEarningsDataItems,
            deductions: formatDeductionsDataItems,
            deductionsAtSource: formatDeductionsAtSourceDataItems,
            nonPeriodicPayments: formatNonPeriodicPaymentDataItems,
            edrEarningsCash: formatEdrEarningsCashDataItems,
            edrDeductionsNonCash: formatEdrDeductionNonCashDataItems,

            savefor: values.savefor,
            notes: values.notes,
            printOnStub: values.printOnStub,
        };
    };

    const onSaveButtonClickHandler = async (selectedItem: any) => {
        const payload = getEarningSummaryPayload();
        console.log("Selected Option:", selectedItem);
        console.log("Payload:", payload);

        if (selectedItem === "Save") {
            submitSaveEarningSummary({ payrollData: [payload] });
        }
        else if (selectedItem === "Save + Payroll") {
            const { employeeId } = payload;
            submitSaveAndPayrollEarningSummary({
                clientId: clientId,
                payPeriodEndDate: payPeriodEndDate,
                payrollSheduleId: selectedPayPeriodId,
                employeeIds: [employeeId],
            });
        }
    };

    useEffect(() => {
        dispatch(
            setSelectedActivityLogActions({
                entityId1: id ? id : "",
                entityId2: "",
                entityId3: "",
                screenName: CLIENT_SETUP_SCREEN_SLUG,
                accountantName: clientName,
                activityType:
                    "activity_type=create&activity_type=updated&activity_type=delete",
                activityLogVisibility: true,
            })
        );
        dispatch(setSelectedStickyNavBar({ isSticky: true }));
        if (drawerMenuOptions && drawerMenuOptions.length > 0) {
            const enhancedData = enhanceData(drawerMenuOptions);
            dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
        }
    }, [drawerMenuOptions]);

    // const onSaveButtonClickHandler = async () => {
    // alert("later")
    // console.log("formValues", formMethods.getValues());
    // console.log("earningsTableUpdatedData", earningsTableUpdatedData);
    // console.log("deductionsTableUpdatedData", deductionsTableUpdatedData);
    // console.log("deductionsAtSourceTableUpdatedData", deductionsAtSourceTableUpdatedData);
    // console.log("nonPeriodicPaymentTableUpdatedData", nonPeriodicPaymentTableUpdatedData);
    // console.log("edrEarningsCashTableUpdatedData", edrEarningsCashTableUpdatedData);
    // c
    // onsole.log("edrDeductionsNonCashTableUpdatedData", edrDeductionsNonCashTableUpdatedData);
    // }
    return (
        <Grid>
            <div
                style={{
                    margin: "15px 0",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
                }}
            >
                {"Earning Summary"}
            </div>
            <BackdropCircularProgress
                open={isSubmitting || isPendingPayFrequencyDDL || isPendingPayrollPeriodsDDL || isEmployeeDDLLoading || isSubmittingSave || isSubmittingSaveAndPayroll || isPending}
                testId="earning summary Setup"
            />
            <Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <FormProvider {...formMethods}>
                        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                            <Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <Grid
                                    // size={{ xs: 12, sm: 12, md: 8.5, lg: 8, xl: 8 }}
                                    size={{ xs: 12, sm: 12, md: 9, lg: 9, xl: 9 }}
                                    sx={{ background: "#ffffff", }}>
                                    <StandardPanel
                                        title=""
                                        testId={`Earnings-info`}
                                        variant="outlined"
                                        borderBottom={true}
                                        borderHide={true}
                                        showTitle={false}
                                    >
                                        <EarningSummaryHeaderPanel
                                            payFrequencyDDLItems={payFrequencyDDLItems}
                                            onFrequencyChangeHandler={onFrequencyChangeHandler}
                                            payrollPeriodsDDL={payrollPeriodsDDL && payrollPeriodsDDL}
                                            onPayrollPeriodsDDLChangeHandler={onPayrollPeriodsDDLChangeHandler}
                                            employeesDDL={employeeDDL}
                                            onEmployeeSelectionChangeHandler={onEmployeeSelectionChangeHandler}
                                            formMethods={formMethods}
                                        />
                                    </StandardPanel>
                                    {selectedEmployeeId && <TwoColumnLayout
                                        leftColumnContent={
                                            <>
                                                <StandardPanel
                                                    title="Earnings"
                                                    testId={`Earnings-info`}
                                                    variant="outlined"
                                                    borderBottom={true}
                                                    borderHide={true}
                                                >
                                                    <EarningsInfoFieldsTable
                                                        earningsTableUpdatedData={(data: any) => setEarningsTableUpdatedData(data)}
                                                        earningsFieldsDataItems={earningsFieldsDataItems}// change later 
                                                    />
                                                </StandardPanel>
                                                <StandardPanel
                                                    title="Non Periodic Payments etc"
                                                    testId={`Non-Periodic-Payments-etc-info`}
                                                    variant="outlined"
                                                    borderBottom={true}
                                                    borderHide={true}
                                                >
                                                    <NonPeriodicPaymentInfoFieldsTable
                                                        nonPeriodicTableUpdatedData={(data: any) => setNonPeriodicPaymentTableUpdatedData(data)}
                                                        nonPeriodicFieldsDataItems={nonPeriodicPaymentFieldsDataItems}// change later 
                                                    />
                                                </StandardPanel>
                                                <StandardPanel
                                                    title="EDR Earnings (Cash)"
                                                    testId={`EDR-Earnings-(Cash)-info`}
                                                    variant="outlined"
                                                    borderBottom={true}
                                                    borderHide={true}
                                                >
                                                    <EDREarningsCashFieldsTable
                                                        edrEarningsCashTableUpdatedData={(data: any) => setEdrEarningsCashTableUpdatedData(data)}
                                                        edrEarningsCashFieldsDataItems={edrEarningsCashFieldsDataItems} // change later 
                                                    />
                                                </StandardPanel>
                                                <StandardPanel
                                                    title="Notes"
                                                    testId={`Notes-info`}
                                                    variant="outlined"
                                                    borderBottom={false}
                                                    borderHide={true}
                                                >
                                                    <NotesInfoFieldsPanel />
                                                </StandardPanel>
                                            </>
                                        }
                                        rightColumnContent={
                                            <>
                                                <StandardPanel
                                                    title="Deductions"
                                                    testId={`deductions-info`}
                                                    variant="outlined"
                                                    borderBottom={true}
                                                    borderHide={true}
                                                >
                                                    <DeductionsInfoFieldsTable
                                                        deductionsTableUpdatedData={(data: any) => setDeductionsTableUpdatedData(data)}
                                                        deductionsFieldsDataItems={deductionsFieldsDataItems}// change later 
                                                    />
                                                </StandardPanel>
                                                {/* <StandardPanel
                                                    title="Deductions at Source"
                                                    testId={`Deductions-At-Source-info`}
                                                    variant="outlined"
                                                    borderBottom={true}
                                                    borderHide={true}
                                                >
                                                    <DeductionsAtSourceInfoFieldsTable
                                                        deductionsAtSourceTableUpdatedData={(data: any) => setDeductionsAtSourceTableUpdatedData(data)}
                                                        deductionsAtSourceFieldsDataItems={deductionsAtSourceFieldsDataItems}// change later 
                                                    />
                                                </StandardPanel> */}
                                                <StandardPanel
                                                    title="EDR Deductions (Non-Cash)"
                                                    testId={`EDR-Deductions-(Non-Cash)info`}
                                                    variant="outlined"
                                                    borderBottom={true}
                                                    borderHide={true}
                                                >
                                                    <EDRDeductionsNonCashFieldsTable
                                                        edrDeductionNonCashTableUpdatedData={(data: any) => setEdrDeductionsNonCashTableUpdatedData(data)}
                                                        edrDeductionNonCashFieldsDataItems={edrDeductionsNonCashFieldsDataItems} // change later 
                                                    />

                                                    <div style={{ padding: "0.5rem 0", marginTop: "0.5rem" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem" }}>
                                                            <span >Accrued Cash Advance</span>
                                                            <span>{"0.00"}</span>
                                                        </div>
                                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem" }}>
                                                            <span >Cash Advance Deduction</span>
                                                            <span>{"0.00"}</span>
                                                        </div>
                                                    </div>
                                                </StandardPanel>
                                                <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}
                                                    sx={{
                                                        padding: "2rem 1rem",
                                                        marginLeft: "auto",
                                                        textAlign: "right",

                                                    }}
                                                >
                                                    <span style={{ fontSize: "0.875rem", fontWeight: "bold" }}>Total Earnings: $ {"528.00"}</span>
                                                    <br />
                                                    <span style={{ fontSize: "0.875rem", fontWeight: "bold" }}>Total Deductions: $ {"0.00"}</span>
                                                    <br />
                                                    <span style={{ fontSize: "0.875rem", fontWeight: "bold" }}>Net Pay for the Period: $ {"528.00"}</span>
                                                </Grid>

                                            </>
                                        }
                                        toolbar={
                                            <EarningSummarySetupToolbar
                                                onSaveButtonClickHandler={onSaveButtonClickHandler}
                                            />
                                        }
                                        testId="form-layout"
                                        showBorder={false}
                                        toolbarPosition="Bottom"
                                    />}
                                </Grid>
                            </Grid>
                        </form>
                    </FormProvider>
                </Grid>
            </Grid>

            <EnhancedSnackbar
                message={snackbarData.message}
                onCloseHandler={onSnackbarClosed}
                severity={snackbarData.severity}
                testId={"earning-summary-snackbar"}
                open={snackbarData.open}
            />
        </Grid>
    );
};
